/* eslint-disable max-lines-per-function */
import { FLOOR_TEXTURES, SKY_TEXTURES } from '../../core/constants/texture-options';
import { ToolDefinition, ToolSource } from '../types/tool.types';

// Extract keys for enums
const SKY_TEXTURE_KEYS = SKY_TEXTURES.map((t) => t.key);
const FLOOR_TEXTURE_KEYS = FLOOR_TEXTURES.map((t) => t.key);

// Valid presets for geo-lighting tools
const TIME_PRESETS = ['realtime', 'sunrise', 'morning', 'afternoon', 'evening', 'sunset', 'night'] as const;
const WEATHER_PRESETS = [
	'clear-sunny',
	'overcast',
	'cloudy',
	'twilight',
	'rainy',
	'golden-hour',
	'blue-hour'
] as const;

const TOOL_SOURCE: ToolSource = '3d-component';

export const getToolDefinitions = (): ToolDefinition[] => {
	const tools: ToolDefinition[] = [
		// ─────────────────────────────────────────────────────────────────────────
		// Camera Controls
		// ─────────────────────────────────────────────────────────────────────────
		{
			name: 'rotate_camera',
			description: 'Rotate the 3D camera to a specific angle and optional elevation.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					angle: {
						type: 'number',
						description: 'Azimuth angle in degrees (0 = front, 90 = left, -90 = right, 180 = back)'
					},
					elevation: {
						type: 'number',
						description: 'Polar angle in degrees (0 = bottom, 90 = horizontal, 180 = top)',
						default: 90
					},
					duration: {
						type: 'number',
						description: 'Animation duration in milliseconds. Set to 0 for instant, or 500-2000 for smooth transition.',
						default: 0
					}
				},
				required: ['angle']
			}
		},
		{
			name: 'look_at_object',
			description: 'Rotate the camera to look at a specific object without moving the camera position. Uses a smooth animated transition.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					objectId: {
						type: 'string',
						description: 'The unique ID of the object to look at (e.g., "grill-1", "island-1", "cabinet-2")'
					},
					duration: {
						type: 'number',
						description: 'Animation duration in milliseconds. Defaults to 1000ms.',
						default: 1000
					}
				},
				required: ['objectId']
			}
		},
		{
			name: 'reset_view',
			description: 'Reset the camera view to the default position. Recalculates the bounding box of all islands and smoothly animates the camera to frame the entire scene, just like the initial render.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'zoom_to',
			description: 'Zoom the camera to focus on a specific island or fit all islands in view.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					target: {
						type: 'string',
						description:
							'Target to zoom to: "all" to fit all islands in view, or an island ID like "island-1", "island-2", etc.'
					}
				},
				required: ['target']
			}
		},

		// ─────────────────────────────────────────────────────────────────────────
		// Rendering Controls
		// ─────────────────────────────────────────────────────────────────────────
		{
			name: 'set_render_quality',
			description: 'Set rendering quality level (0-10). 0 enables wireframe mode.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					quality: {
						type: 'number',
						description:
							'Render quality: 0=wireframe, 1-3=low (mobile), 4-6=medium, 7-8=high, 9-10=ultra',
						min: 0,
						max: 10
					}
				},
				required: ['quality']
			}
		},
		{
			name: 'set_texture_quality',
			description:
				'Set texture quality tier (0-10, or null to follow render_quality). Decoupled from set_render_quality so mobile devices can cap texture memory (especially large sky HDRs) while keeping shader/shadow quality high. 0 disables sky textures entirely; null follows render_quality.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					quality: {
						type: ['number', 'null'],
						description:
							'Texture quality: null=follow render_quality, 0=no sky texture, 1-3=low (mobile), 4-6=medium, 7-8=high, 9-10=ultra',
						min: 0,
						max: 10
					}
				},
				required: ['quality']
			}
		},
		{
			name: 'set_debug_mode',
			description:
				'Enable or disable debug visualization overlays.\n' +
				'Use when debugging scene issues like cutout placement, lighting, or bounding boxes.\n' +
				'Do NOT use for normal viewing - only for troubleshooting.\n' +
				'Returns {enabled, options} confirming debug state.',
			parameters: {
				type: 'object',
				properties: {
					enabled: {
						type: 'boolean',
						description: 'true to enable debug visualizations, false to disable'
					},
					options: {
						type: 'object',
						description: 'Optional feature toggles (all default to true when debug mode is enabled)',
						properties: {
							cutouts: {
								type: 'boolean',
								description: 'Show semi-transparent red boxes where cutouts are applied',
								default: true
							},
							lights: {
								type: 'boolean',
								description: 'Show light helper gizmos',
								default: true
							},
							camera: {
								type: 'boolean',
								description: 'Show camera helper gizmos',
								default: true
							},
							bounds: {
								type: 'boolean',
								description: 'Show bounding box visualizations',
								default: true
							}
						}
					}
				},
				required: ['enabled']
			}
		},

		// ─────────────────────────────────────────────────────────────────────────
		// Object Interaction
		// ─────────────────────────────────────────────────────────────────────────
		{
			name: 'highlight_object',
			description: 'Highlight a specific object in the 3D scene by its ID.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					objectId: {
						type: 'string',
						description: 'The unique ID of the object to highlight (e.g., "grill-1", "cabinet-2")'
					}
				},
				required: ['objectId']
			}
		},
		{
			name: 'clear_highlights',
			description: 'Clear all current highlights in the 3D scene.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'get_island_selection',
			description:
				'Show numbered labels floating above each island in the 3D scene.\n' +
				'Returns a mapping of number to island ID immediately.\n' +
				'Pass islandIds to show labels for specific islands only.\n' +
				'Clicking a number label in the scene selects that island and hides labels.\n' +
				'Call clear_selections to remove the labels programmatically.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					islandIds: {
						type: 'array',
						description: 'Optional array of island IDs to show labels for. If omitted, shows all islands.',
						items: {
							type: 'string'
						}
					}
				}
			}
		},
		{
			name: 'get_appliance_selection',
			description:
				'Show numbered labels in front of appliances in the 3D scene.\n' +
				'Returns a mapping of number to appliance ID immediately.\n' +
				'Clicking a number label selects that appliance and hides labels.\n' +
				'Pass applianceIds to show labels for specific appliances only.\n' +
				'Pass islandId to show labels for appliances on a specific island only.\n' +
				'Call clear_selections to remove the labels programmatically.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					applianceIds: {
						type: 'array',
						description: 'Optional array of appliance IDs to show labels for. If omitted, shows all appliances.',
						items: {
							type: 'string'
						}
					},
					islandId: {
						type: 'string',
						description: 'Optional island ID to filter appliances. Only show appliances on this island.'
					}
				}
			}
		},
		{
			name: 'clear_selections',
			description: 'Remove all floating selection labels (island and appliance) from the 3D scene.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},

		// ─────────────────────────────────────────────────────────────────────────
		// Screenshot
		// ─────────────────────────────────────────────────────────────────────────
		{
			name: 'take_screenshot',
			description: 'Capture a screenshot of the current 3D view.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					pixelRatio: {
						type: 'number',
						description: 'Resolution multiplier: 1=screen resolution, 2=2x (default), 4=4x for print quality',
						default: 2
					}
				}
			}
		},

		// ─────────────────────────────────────────────────────────────────────────
		// Environment Textures
		// ─────────────────────────────────────────────────────────────────────────
		{
			name: 'get_sky_options',
			description:
				'[CLIENT ASYNC] Gets available sky/environment texture options.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_sky_options] in history.\n\n' +
				'WORKFLOW:\n' +
				'1. Call this tool to get available options\n' +
				'2. Data returns via TOOL_RESULT\n' +
				'3. Present selection cards OR auto-apply if single match',
			meta: {
				isAsync: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'get_floor_options',
			description:
				'[CLIENT ASYNC] Gets available floor texture options.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_floor_options] in history.\n\n' +
				'WORKFLOW:\n' +
				'1. Call this tool to get available options\n' +
				'2. Data returns via TOOL_RESULT\n' +
				'3. Present selection cards OR auto-apply if single match',
			meta: {
				isAsync: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'set_sky_texture',
			description:
				'[CLIENT] Sets the sky/environment texture. REQUIRES valid key from get_sky_options.\n\n' +
				'WORKFLOW:\n' +
				'1. Call get_sky_options first\n' +
				'2. Match user request to returned options\n' +
				'3. Apply with matched texture key\n\n' +
				'NEVER fabricate texture keys.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					texture: {
						type: 'string',
						description: 'Sky texture key from available options',
						enum: SKY_TEXTURE_KEYS
					}
				},
				required: ['texture']
			}
		},
		{
			name: 'set_floor_texture',
			description:
				'[CLIENT] Sets the floor texture. REQUIRES valid key from get_floor_options.\n\n' +
				'WORKFLOW:\n' +
				'1. Call get_floor_options first\n' +
				'2. Match user request to returned options\n' +
				'3. Apply with matched texture key\n\n' +
				'NEVER fabricate texture keys.',
			meta: {
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					texture: {
						type: 'string',
						description: 'Floor texture key from available options',
						enum: FLOOR_TEXTURE_KEYS
					}
				},
				required: ['texture']
			}
		},

		// ─────────────────────────────────────────────────────────────────────────
		// Geographic Lighting
		// ─────────────────────────────────────────────────────────────────────────
		{
			name: 'set_geo_location',
			description:
				'Set geographic coordinates for realistic sun position lighting.\n' +
				'Use when the user wants outdoor scenes with accurate sun angles for a real location.\n' +
				'Call set_time_of_day after this to control the sun position.\n' +
				'Do NOT use for indoor scenes or stylized lighting - use set_sky_texture instead.\n' +
				'Returns {latitude, longitude, enabled} confirming the location.',
			parameters: {
				type: 'object',
				properties: {
					latitude: {
						type: 'number',
						description: 'Latitude in degrees: -90 (South Pole) to 90 (North Pole)',
						min: -90,
						max: 90
					},
					longitude: {
						type: 'number',
						description: 'Longitude in degrees: -180 (West) to 180 (East)',
						min: -180,
						max: 180
					},
					enabled: {
						type: 'boolean',
						description: 'Enable geographic lighting (true) or disable it (false)',
						default: true
					}
				},
				required: ['latitude', 'longitude']
			}
		},
		{
			name: 'set_time_of_day',
			description:
				'Set the time of day for geographic lighting (sun position).\n' +
				'Use after set_geo_location to control when the sun is positioned.\n' +
				'Requires geographic lighting to be enabled first.\n' +
				'Use "realtime" for current actual time at the location.\n' +
				'Returns {time, preset: true} for presets or {date, manual: true} for custom dates.',
			parameters: {
				type: 'object',
				properties: {
					time: {
						type: 'string',
						description: 'Time preset for lighting conditions',
						enum: [...TIME_PRESETS]
					},
					date: {
						type: 'string',
						description:
							'ISO date string for manual time (e.g., "2025-06-21T14:30:00"). Use instead of time preset for precise control.'
					}
				}
			}
		},
		{
			name: 'set_weather',
			description:
				'Set atmospheric/weather preset affecting lighting mood and intensity.\n' +
				'Use after set_geo_location to add atmospheric effects.\n' +
				'Modifies the lighting to simulate different weather conditions.\n' +
				'Returns {preset} confirming the applied weather.',
			parameters: {
				type: 'object',
				properties: {
					preset: {
						type: 'string',
						description: 'Weather condition preset affecting lighting atmosphere',
						enum: [...WEATHER_PRESETS]
					}
				},
				required: ['preset']
			}
		}
	];
	return tools.map((tool) => ({ ...tool, source: TOOL_SOURCE }));
};
